import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

function nextUrlToPath(nextUrl) {
  const raw = String(nextUrl || "").trim();
  if (!raw) return null;
  if (raw.startsWith("/")) return raw;
  try {
    const u = new URL(raw, window.location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return null;
  }
}

async function fetchAllPages(path, { maxPages = 10 } = {}) {
  const all = [];
  let next = path;
  let pages = 0;
  while (next && pages < maxPages) {
    // eslint-disable-next-line no-await-in-loop
    const data = await apiFetch(next, { method: "GET" });
    const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    all.push(...list);
    if (Array.isArray(data)) break;
    next = nextUrlToPath(data?.next);
    pages += 1;
  }
  return all;
}

export const fetchPurchaseRequests = createAsyncThunk(
  "purchaseRequests/fetchPurchaseRequests",
  async ({ search = "", status = "", createdFrom = null, createdTo = null } = {}) => {
    const qp = new URLSearchParams();
    if (String(search || "").trim()) qp.set("search", String(search || "").trim());
    if (String(status || "").trim()) qp.set("status", String(status || "").trim());
    if (createdFrom) qp.set("created_at__gte", createdFrom);
    if (createdTo) qp.set("created_at__lte", createdTo);
    qp.set("ordering", "-created_at");
    const path = `/api/product/purchase-requests/${qp.toString() ? `?${qp.toString()}` : ""}`;
    return await fetchAllPages(path, { maxPages: 10 });
  }
);

export const fetchPurchaseRequestDetail = createAsyncThunk(
  "purchaseRequests/fetchPurchaseRequestDetail",
  async ({ requestId }) => {
    return await apiFetch(`/api/product/purchase-requests/${requestId}/`, { method: "GET" });
  }
);

export const fetchStoresForPurchaseRequest = createAsyncThunk(
  "purchaseRequests/fetchStoresForPurchaseRequest",
  async ({ search = "" } = {}) => {
    const qp = new URLSearchParams();
    qp.set("ordering", "name");
    if (String(search || "").trim()) qp.set("search", String(search || "").trim());
    const path = `/api/product/stores/?${qp.toString()}`;
    return await fetchAllPages(path, { maxPages: 10 });
  }
);

export const createPurchaseRequest = createAsyncThunk(
  "purchaseRequests/createPurchaseRequest",
  async ({ store, comment = "" }, { rejectWithValue }) => {
    try {
      const created = await apiFetch("/api/product/purchase-requests/", {
        method: "POST",
        body: { store, comment: String(comment || "") },
      });
      return created;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to create purchase request" });
    }
  }
);

export const updatePurchaseRequest = createAsyncThunk(
  "purchaseRequests/updatePurchaseRequest",
  async ({ requestId, store, comment = "" }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/purchase-requests/${requestId}/`, {
        method: "PATCH",
        body: { store, comment: String(comment || "") },
      });
      return updated;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to update purchase request" });
    }
  }
);

const slice = createSlice({
  name: "purchaseRequests",
  initialState: {
    stores: {
      list: [],
      status: "idle",
      error: null,
      lastSearch: "",
    },
    requests: {
      list: [],
      status: "idle",
      error: null,
      lastSearch: "",
    },
    requestById: {},
    requestStatusById: {},
    requestErrorById: {},
    createStatus: "idle",
    createError: null,
    updateStatusById: {},
    updateErrorById: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStoresForPurchaseRequest.pending, (state, action) => {
        state.stores.status = "loading";
        state.stores.error = null;
        state.stores.lastSearch = action.meta.arg?.search || "";
      })
      .addCase(fetchStoresForPurchaseRequest.fulfilled, (state, action) => {
        state.stores.status = "succeeded";
        state.stores.list = action.payload || [];
      })
      .addCase(fetchStoresForPurchaseRequest.rejected, (state, action) => {
        state.stores.status = "failed";
        state.stores.error = action.error?.message || "Failed to load stores";
      })
      .addCase(fetchPurchaseRequests.pending, (state, action) => {
        state.requests.status = "loading";
        state.requests.error = null;
        state.requests.lastSearch = action.meta.arg?.search || "";
      })
      .addCase(fetchPurchaseRequests.fulfilled, (state, action) => {
        state.requests.status = "succeeded";
        state.requests.list = action.payload || [];
      })
      .addCase(fetchPurchaseRequests.rejected, (state, action) => {
        state.requests.status = "failed";
        state.requests.error = action.error?.message || "Failed to load purchase requests";
      })
      .addCase(fetchPurchaseRequestDetail.pending, (state, action) => {
        const id = String(action.meta.arg.requestId);
        state.requestStatusById[id] = "loading";
        state.requestErrorById[id] = null;
      })
      .addCase(fetchPurchaseRequestDetail.fulfilled, (state, action) => {
        const id = String(action.meta.arg.requestId);
        state.requestStatusById[id] = "succeeded";
        state.requestById[id] = action.payload;
      })
      .addCase(fetchPurchaseRequestDetail.rejected, (state, action) => {
        const id = String(action.meta.arg.requestId);
        state.requestStatusById[id] = "failed";
        state.requestErrorById[id] = action.error?.message || "Failed to load purchase request";
      })
      .addCase(createPurchaseRequest.pending, (state) => {
        state.createStatus = "saving";
        state.createError = null;
      })
      .addCase(createPurchaseRequest.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        const created = action.payload;
        if (created?.id != null) state.requestById[String(created.id)] = created;
      })
      .addCase(createPurchaseRequest.rejected, (state, action) => {
        state.createStatus = "failed";
        const payload = action.payload;
        state.createError =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to create purchase request";
      })
      .addCase(updatePurchaseRequest.pending, (state, action) => {
        const id = String(action.meta.arg.requestId);
        state.updateStatusById[id] = "saving";
        state.updateErrorById[id] = null;
      })
      .addCase(updatePurchaseRequest.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated?.id ?? action.meta.arg.requestId);
        state.updateStatusById[id] = "succeeded";
        if (updated) state.requestById[id] = updated;
        state.requests.list = (state.requests.list || []).map((r) => (r.id === updated?.id ? updated : r));
      })
      .addCase(updatePurchaseRequest.rejected, (state, action) => {
        const id = String(action.meta.arg.requestId);
        state.updateStatusById[id] = "failed";
        const payload = action.payload;
        state.updateErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to update purchase request";
      });
  },
});

export default slice.reducer;

export const selectPurchaseRequestStores = (state) => state.purchaseRequests.stores.list || [];
export const selectPurchaseRequestStoresStatus = (state) => state.purchaseRequests.stores.status || "idle";
export const selectPurchaseRequestStoresError = (state) => state.purchaseRequests.stores.error || null;

export const selectPurchaseRequests = (state) => state.purchaseRequests.requests.list || [];
export const selectPurchaseRequestsStatus = (state) => state.purchaseRequests.requests.status || "idle";
export const selectPurchaseRequestsError = (state) => state.purchaseRequests.requests.error || null;

export const selectPurchaseRequestById = (id) => (state) => state.purchaseRequests.requestById[String(id)] || null;
export const selectPurchaseRequestStatusById = (id) => (state) =>
  state.purchaseRequests.requestStatusById[String(id)] || "idle";
export const selectPurchaseRequestErrorById = (id) => (state) =>
  state.purchaseRequests.requestErrorById[String(id)] || null;

export const selectCreatePurchaseRequestStatus = (state) => state.purchaseRequests.createStatus || "idle";
export const selectCreatePurchaseRequestError = (state) => state.purchaseRequests.createError || null;

export const selectUpdatePurchaseRequestStatusById = (id) => (state) =>
  state.purchaseRequests.updateStatusById[String(id)] || "idle";
export const selectUpdatePurchaseRequestErrorById = (id) => (state) =>
  state.purchaseRequests.updateErrorById[String(id)] || null;
